import { useMemo, useState, useCallback } from 'react';
import { Button, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import printerIcon from '../../../../../../../img/printer.svg';
import { createPaginationConfig } from '@client/utils/table/tableUtils';
import { SaveSearchButton } from '@client/components/SavedSearch';
import ShareButton from '@client/components/ShareButton';
import TutorialButton from '@client/components/ui/TutorialButton';
import { deliverPdfBlob } from '@client/utils/pdfDelivery';

const PARAM_SEPARATOR = '|';

/**
 * Results header with title, print button, and count display
 * @param {Object} props - Component props
 * @param {Object} props.data - Search results data
 * @param {number} props.data.count - Total number of results
 * @param {string} props.data.query_time - Query execution time
 * @param {Function} props.onPageChange - Page change handler
 * @returns {JSX.Element} Results header
 */
const ResultsHeader = ({ data, onPageChange, filters, hideCultures, selectedRowKeys = [], showSelectedOnly = false }) => {
  const { t } = useTranslation();
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  const pageSize = data?.size || 20;
  const totalCount = data?.count || 0;

  const pagination = useMemo(
    () => createPaginationConfig(data, pageSize, t, {
      showSizeChanger: false,
      showQuickJumper: false,
    }),
    [data, pageSize, t]
  );

  const handlePageChange = (page, newPageSize) => {
    if (onPageChange) {
      onPageChange({ page, size: newPageSize });
    }
  };

  const handlePdfDownload = useCallback(async () => {
    setIsPdfLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(PARAM_SEPARATOR));
        } else if (value !== null && value !== undefined && value !== '') {
          params.set(key, value);
        }
      });
      // Remove pagination params - PDF fetches all results
      params.delete('page');
      params.delete('size');

      if (hideCultures) {
        params.set('hide_cultures', '1');
      }

      if (showSelectedOnly && selectedRowKeys.length > 0) {
        params.set('selected_ids', selectedRowKeys.join(PARAM_SEPARATOR));
      }

      const url = `/api/active-substances/filter/pdf/?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await response.blob();
      deliverPdfBlob(blob, response, 'matieres-actives.pdf');
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      setIsPdfLoading(false);
    }
  }, [filters, hideCultures, showSelectedOnly, selectedRowKeys]);

  return (
    <div className="flex flex-col gap-4 mb-6 sm:mb-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        <h4 className="normal-case text-lg sm:text-xl font-medium">
          {t('active-substance|Search results')}
        </h4>

        <div className="flex flex-col gap-2 w-full sm:flex-row sm:w-auto sm:gap-2">
          <TutorialButton slotKey="active-substance-search-results" className="w-full sm:w-auto" />
          <Button
            type="default"
            variant="outlined"
            size="large"
            className="print-button-with-icon w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={handlePdfDownload}
            loading={isPdfLoading}
            disabled={isPdfLoading || totalCount === 0}
          >
            {!isPdfLoading && (
              <img src={printerIcon} alt="Print" className="w-4 h-4 print-icon" />
            )}
            {isPdfLoading
              ? t('active-substance|Generating PDF...')
              : t('active-substance|Printable version')}
          </Button>
          <ShareButton className="w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3 font-medium" resetKey={filters} />
          <SaveSearchButton
            searchType="active_substance"
            getCurrentUrl={() => window.location.href}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {totalCount === 0 ? (
          <p className="text-gray-500">0 {t('common|result', 'résultat')}</p>
        ) : (
          <Pagination
            {...pagination}
            onChange={handlePageChange}
            className="custom-pagination !mt-[0]"
          />
        )}
      </div>
    </div>
  );
};

export default ResultsHeader;
